/**
 * AuditRow — строка журнала: иконка действия, подпись + превью payload,
 * автор и время справа. Клик раскрывает полный payload (моно-JSON) —
 * удобно разбирать инциденты не лазя в базу.
 */

import {useState} from 'react';
import {ChevronDown} from 'lucide-react';

import type {AuditEntry} from '@services/audit';
import {cn} from '@lib/utils';

import {actionMeta, fmtAuditTime, payloadPreview} from '../Audit.utils';

type Props = {entry: AuditEntry};

export const AuditRow = ({entry}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const meta = actionMeta(entry.action);
  const preview = payloadPreview(entry.payload);
  const expandable = entry.payload !== null;
  const Icon = meta.icon;

  const handleToggle = () => {
    if (expandable) setExpanded((v) => !v);
  };

  return (
    <div className="border-b border-skr-hairline last:border-b-0">
      <button
        onClick={handleToggle}
        disabled={!expandable}
        className={cn(
          'flex w-full items-center gap-3.5 px-4 py-3 text-left',
          expandable && 'cursor-pointer hover:bg-skr-head',
        )}
      >
        <span
          className={cn(
            'grid size-9 shrink-0 place-items-center rounded-[10px]',
            meta.danger
              ? 'bg-destructive/10 text-destructive'
              : 'bg-skr-icon-bg text-muted-foreground',
          )}
        >
          <Icon className="size-[17px]" strokeWidth={1.8} />
        </span>

        <span className="min-w-0 flex-1 leading-[1.35]">
          <span className="block truncate text-[13.5px] font-semibold text-foreground">
            {meta.label}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            <span className="font-mono">{entry.action}</span>
            {preview !== null && <> · {preview}</>}
          </span>
        </span>

        <span className="shrink-0 text-right leading-[1.4]">
          <span className="block font-mono text-xs text-skr-ink2">{entry.actorLogin}</span>
          <span className="block font-mono text-[11.5px] text-skr-faint">
            {fmtAuditTime(entry.createdAt)}
          </span>
        </span>

        {expandable && (
          <ChevronDown
            className={cn('size-4 shrink-0 text-skr-chevron transition', expanded && 'rotate-180')}
            strokeWidth={2}
          />
        )}
      </button>

      {expanded && entry.payload !== null && (
        <pre className="mx-4 mb-3 overflow-x-auto rounded-[10px] border border-skr-line2 bg-skr-head p-3 font-mono text-[11.5px] leading-[1.5] text-skr-ink2">
          {JSON.stringify(entry.payload, null, 2)}
        </pre>
      )}
    </div>
  );
};
